import { useState, useEffect, useMemo } from "react";
import { getErrorMessage } from "@/lib/errors";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PageHeader } from "@/components/ui/page-header";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { motion } from "framer-motion";
import {
  Plus, Trash2, Pencil, Wallet, CreditCard, Building2, Landmark,
  PiggyBank, Smartphone, Globe, Coins, CircleDollarSign, BadgeDollarSign,
  Banknote, HandCoins, TrendingUp, ShieldCheck, Gift, ShoppingBag, Bitcoin, RefreshCw,
  ChevronsUpDown, Check, X, ChevronUp, ChevronDown, Link2
} from "lucide-react";
import { useAccountFundingSources } from "@/hooks/queries/useAccountsQuery";
import { useAddFundingSource, useRemoveFundingSource, useReorderFundingSource } from "@/hooks/mutations/useAccountMutations";
import { Switch } from "@/components/ui/switch";
import { ALL_CURRENCIES, CRYPTO_CURRENCIES, useExchangeRates } from "@/hooks/useExchangeRates";
import { describeExcluded } from "@/lib/currencyTotals";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { BalanceHistory } from "@/components/Accounts/BalanceHistory";
import { useAccountBalances } from "@/hooks/useAccountBalances";
import { cn } from "@/lib/utils";
import { todayISO } from "@/lib/dates";

interface Transaction {
  id: string;
  type: "income" | "expense";
  amount: number;
  account: string;
  date: string;
  is_backdated?: boolean;
}

const ACCOUNT_ICONS = [
  { name: "wallet", icon: Wallet, label: "Wallet" },
  { name: "credit-card", icon: CreditCard, label: "Credit Card" },
  { name: "building", icon: Building2, label: "Building" },
  { name: "landmark", icon: Landmark, label: "Bank" },
  { name: "piggy-bank", icon: PiggyBank, label: "Savings" },
  { name: "smartphone", icon: Smartphone, label: "Mobile" },
  { name: "globe", icon: Globe, label: "Online" },
  { name: "coins", icon: Coins, label: "Coins" },
  { name: "dollar-sign", icon: CircleDollarSign, label: "Dollar" },
  { name: "badge-dollar", icon: BadgeDollarSign, label: "Badge" },
  { name: "banknote", icon: Banknote, label: "Cash" },
  { name: "hand-coins", icon: HandCoins, label: "Hand Coins" },
  { name: "trending-up", icon: TrendingUp, label: "Investment" },
  { name: "shield-check", icon: ShieldCheck, label: "Secure" },
  { name: "gift", icon: Gift, label: "Gift" },
  { name: "shopping-bag", icon: ShoppingBag, label: "Shopping" },
  { name: "bitcoin", icon: Bitcoin, label: "Bitcoin" },
];

const ACCOUNT_TYPES = [
  { value: "bank", label: "Bank Account" },
  { value: "credit", label: "Credit Card" },
  { value: "cash", label: "Cash" },
  { value: "investment", label: "Investment" },
  { value: "virtual", label: "Virtual Account" },
  { value: "savings", label: "Savings" },
  { value: "crypto", label: "Crypto Wallet" },
  { value: "other", label: "Other" },
];

interface Account {
  id: string;
  name: string;
  parent_account_id: string | null;
  icon: string;
  account_type: string;
  currency: string;
  initial_balance: number;
  manual_exchange_rate: number | null;
  exclude_from_dashboard: boolean;
  balance_as_of_date: string | null;
}

interface AccountWallet {
  id: string;
  account_id: string;
  user_id: string;
  name: string;
  currency: string;
  balance: number;
  manual_exchange_rate: number | null;
}

export default function Accounts() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [wallets, setWallets] = useState<AccountWallet[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [newAccountName, setNewAccountName] = useState("");
  const [parentAccountId, setParentAccountId] = useState<string | null>(null);
  const [selectedIcon, setSelectedIcon] = useState("wallet");
  const [accountType, setAccountType] = useState("bank");
  const [accountCurrency, setAccountCurrency] = useState("USD");
  const [initialBalance, setInitialBalance] = useState("");
  const [balanceAsOfDate, setBalanceAsOfDate] = useState(todayISO());
  const [manualRate, setManualRate] = useState("");
  const [addFSForAccountId, setAddFSForAccountId] = useState<string | null>(null);
  const [addFSSearch, setAddFSSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  
  // Wallet dialog state
  const [walletDialogOpen, setWalletDialogOpen] = useState(false);
  const [walletAccountId, setWalletAccountId] = useState<string | null>(null);
  const [walletName, setWalletName] = useState("");
  const [walletCurrency, setWalletCurrency] = useState("USD");
  const [walletBalance, setWalletBalance] = useState("");
  const [walletManualRate, setWalletManualRate] = useState("");
  const [editingWallet, setEditingWallet] = useState<AccountWallet | null>(null);
  
  const { toast } = useToast();
  const { preferences, formatCurrency, formatNumber } = useUserPreferences();
  const { data: allFundingSources = [] } = useAccountFundingSources();
  const addFundingSourceMutation = useAddFundingSource();
  const removeFundingSourceMutation = useRemoveFundingSource();
  const reorderFundingSourceMutation = useReorderFundingSource();
  
  // Get unique currencies from accounts and wallets for exchange rate fetching
  const accountCurrencies = [
    ...accounts.map(a => a.currency),
    ...wallets.map(w => w.currency)
  ].filter(Boolean);
  const { rates, loading: ratesLoading, refreshRates, convertToBase, sumInBase, ready: ratesReady, lastUpdated } = useExchangeRates(accountCurrencies);

  // Calculate balances from transactions
  const { getAccountBalance, getWalletBalance } = useAccountBalances(
    transactions,
    accounts.map(a => ({ id: a.id, name: a.name, initial_balance: a.initial_balance, balance_as_of_date: a.balance_as_of_date })),
    wallets.map(w => ({ id: w.id, account_id: w.account_id, name: w.name, balance: w.balance }))
  );

  const fetchAccounts = async () => {
    try {
      const { data, error } = await supabase
        .from("accounts")
        .select("*")
        .order("created_at", { ascending: true });

      if (error) throw error;
      setAccounts(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const fetchWallets = async () => {
    try {
      const { data, error } = await supabase
        .from("wallets")
        .select("*")
        .order("created_at", { ascending: true });

      if (error) throw error;
      setWallets(data || []);
    } catch (error) {
      console.error("Error fetching wallets:", error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const { data, error } = await supabase
        .from("transactions")
        .select("id, type, amount, account, date, is_backdated");

      if (error) throw error;
      setTransactions((data || []) as Transaction[]);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    }
  };

  useEffect(() => {
    fetchAccounts();
    fetchWallets();
    fetchTransactions();
  }, []);

  const resetForm = () => {
    setNewAccountName("");
    setParentAccountId(null);
    setSelectedIcon("wallet");
    setAccountType("bank");
    setAccountCurrency(preferences?.currency || "USD");
    setInitialBalance("");
    setBalanceAsOfDate(todayISO());
    setManualRate("");
    setEditingAccount(null);
  };

  const resetWalletForm = () => {
    setWalletName("");
    setWalletCurrency(preferences?.currency || "USD");
    setWalletBalance("");
    setWalletManualRate("");
    setEditingWallet(null);
    setWalletAccountId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAccountName.trim()) return;

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      if (editingAccount) {
        const { error } = await supabase
          .from("accounts")
          .update({
            name: newAccountName.trim(),
            parent_account_id: parentAccountId,
            icon: selectedIcon,
            account_type: accountType,
            currency: accountCurrency,
            initial_balance: initialBalance ? parseFloat(initialBalance) : 0,
            balance_as_of_date: balanceAsOfDate || null,
            manual_exchange_rate: manualRate ? parseFloat(manualRate) : null,
          })
          .eq("id", editingAccount.id);

        if (error) throw error;
        toast({ title: "Success", description: "Account updated successfully" });
      } else {
        const { error } = await supabase.from("accounts").insert({
          user_id: user.id,
          name: newAccountName.trim(),
          parent_account_id: parentAccountId,
          icon: selectedIcon,
          account_type: accountType,
          currency: accountCurrency,
          initial_balance: initialBalance ? parseFloat(initialBalance) : 0,
          balance_as_of_date: balanceAsOfDate || null,
          manual_exchange_rate: manualRate ? parseFloat(manualRate) : null,
        });

        if (error) throw error;
        toast({ title: "Success", description: "Account added successfully" });
      }

      resetForm();
      setDialogOpen(false);
      fetchAccounts();
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (account: Account) => {
    setEditingAccount(account);
    setNewAccountName(account.name);
    setParentAccountId(account.parent_account_id);
    setSelectedIcon(account.icon || "wallet");
    setAccountType(account.account_type || "bank");
    setAccountCurrency(account.currency || "USD");
    setInitialBalance(account.initial_balance?.toString() || "");
    setBalanceAsOfDate(account.balance_as_of_date || todayISO());
    setManualRate(account.manual_exchange_rate?.toString() || "");
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from("accounts").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Success", description: "Account deleted" });
      fetchAccounts();
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const handleToggleDashboardVisibility = async (account: Account) => {
    try {
      const { error } = await supabase
        .from("accounts")
        .update({ exclude_from_dashboard: !account.exclude_from_dashboard })
        .eq("id", account.id);

      if (error) throw error;
      
      toast({ 
        title: account.exclude_from_dashboard ? "Included in dashboard" : "Excluded from dashboard",
        description: `${account.name} ${account.exclude_from_dashboard ? "will now" : "will no longer"} appear in dashboard stats`
      });
      fetchAccounts();
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  // Wallet CRUD handlers
  const handleWalletSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!walletName.trim() || !walletAccountId) return;

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      if (editingWallet) {
        const { error } = await supabase
          .from("wallets")
          .update({
            name: walletName.trim(),
            currency: walletCurrency,
            balance: parseFloat(walletBalance) || 0,
            manual_exchange_rate: walletManualRate ? parseFloat(walletManualRate) : null,
          })
          .eq("id", editingWallet.id);

        if (error) throw error;
        toast({ title: "Success", description: "Wallet updated successfully" });
      } else {
        const { error } = await supabase.from("wallets").insert({
          user_id: user.id,
          account_id: walletAccountId,
          name: walletName.trim(),
          currency: walletCurrency,
          balance: parseFloat(walletBalance) || 0,
          manual_exchange_rate: walletManualRate ? parseFloat(walletManualRate) : null,
        });

        if (error) throw error;
        toast({ title: "Success", description: "Wallet added successfully" });
      }

      resetWalletForm();
      setWalletDialogOpen(false);
      fetchWallets();
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddWallet = (accountId: string) => {
    setWalletAccountId(accountId);
    setWalletCurrency(preferences?.currency || "USD");
    setWalletDialogOpen(true);
  };

  const handleEditWallet = (wallet: AccountWallet) => {
    setEditingWallet(wallet);
    setWalletAccountId(wallet.account_id);
    setWalletName(wallet.name);
    setWalletCurrency(wallet.currency);
    setWalletBalance(wallet.balance.toString());
    setWalletManualRate(wallet.manual_exchange_rate?.toString() || "");
    setWalletDialogOpen(true);
  };

  const handleDeleteWallet = async (id: string) => {
    try {
      const { error } = await supabase.from("wallets").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Success", description: "Wallet deleted" });
      fetchWallets();
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const getAccountWallets = (accountId: string) => wallets.filter(w => w.account_id === accountId);

  const getIconComponent = (iconName: string) => {
    const iconData = ACCOUNT_ICONS.find(i => i.name === iconName);
    return iconData ? iconData.icon : Wallet;
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      bank: "bg-primary/15 text-primary-text",
      credit: "bg-expense/15 text-expense-text",
      cash: "bg-income/15 text-income-text",
      investment: "bg-accent/15 text-accent",
      virtual: "bg-purple-500/15 text-purple-500",
      savings: "bg-teal-500/15 text-teal-500",
      crypto: "bg-amber-500/15 text-amber-500",
      other: "bg-muted text-muted-foreground",
    };
    return colors[type] || colors.other;
  };

  const getCurrencyInfo = (code: string) => {
    return ALL_CURRENCIES.find(c => c.code === code);
  };

  const isCrypto = (code: string) => CRYPTO_CURRENCIES.some(c => c.code === code);

  const baseCurrency = preferences?.currency || "USD";

  const rootAccounts = accounts.filter(a => !a.parent_account_id);
  const getChildAccounts = (parentId: string) => accounts.filter(a => a.parent_account_id === parentId);

  // Calculate summary totals. Balances with no usable rate are left out and named,
  // never added as if they were the base currency (D-056).
  const summaryTotals = useMemo(() => {
    const visibleAccounts = accounts.filter(account => !account.exclude_from_dashboard);
    const accountHoldings = visibleAccounts.map(account => ({
      amount: getAccountBalance({ id: account.id, name: account.name, initial_balance: account.initial_balance }),
      currency: account.currency,
      manualRate: account.manual_exchange_rate,
    }));
    const walletHoldings = wallets.flatMap(wallet => {
      const account = accounts.find(a => a.id === wallet.account_id);
      if (!account || account.exclude_from_dashboard) return [];
      return [{
        amount: getWalletBalance({ id: wallet.id, account_id: wallet.account_id, name: wallet.name, balance: wallet.balance }, account.name),
        currency: wallet.currency,
        manualRate: wallet.manual_exchange_rate,
      }];
    });

    const total = sumInBase(accountHoldings);
    const crypto = sumInBase([...accountHoldings, ...walletHoldings].filter(h => isCrypto(h.currency)));

    return {
      totalBalance: total.total,
      totalExcluded: total.excluded,
      cryptoHoldings: crypto.total,
      cryptoExcluded: crypto.excluded,
      totalAccounts: accounts.length
    };
  }, [accounts, wallets, getAccountBalance, getWalletBalance, sumInBase, isCrypto]);

  // "Refresh Rates" says how it went, including which currencies still have no rate.
  const handleRefreshRates = async () => {
    const { ok, missing } = await refreshRates();
    if (!ok) {
      toast({ title: "Couldn't load exchange rates", description: "The rate service didn't answer. Totals leave out amounts that can't be converted. Try again later.", variant: "destructive" });
    } else if (missing.length > 0) {
      toast({ title: "Some rates are unavailable", description: `No rate for ${missing.join(", ")}. Those amounts are left out of the totals.` });
    } else {
      toast({ title: "Exchange rates updated" });
    }
  };

  // A per-row conversion: say so plainly when there is no rate (D-056).
  const formatConverted = (value: number | null) =>
    !ratesReady ? "..." : value === null ? "rate unavailable" : formatCurrency(value);

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl space-y-6">
      <PageHeader
        icon={Wallet}
        title="Accounts"
        description="Manage your bank accounts, cards, and crypto wallets"
      >
        {accounts.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshRates}
            disabled={ratesLoading}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${ratesLoading ? 'animate-spin' : ''}`} />
            Refresh Rates
          </Button>
        )}
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="gap-2 shadow-md">
              <Plus className="h-4 w-4" />
              Add Account
            </Button>
          </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="text-lg font-bold">{editingAccount ? "Edit Account" : "Add New Account"}</DialogTitle>
              </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="accountName">Account Name</Label>
                <Input
                  id="accountName"
                  placeholder="e.g., Main Checking, PayPal, Holiday Fund"
                  value={newAccountName}
                  onChange={(e) => setNewAccountName(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Account Type</Label>
                  <Select 
                    value={accountType} 
                    onValueChange={(value) => {
                      setAccountType(value);
                      if (value === "crypto" && !isCrypto(accountCurrency)) {
                        setAccountCurrency("BTC");
                        setSelectedIcon("bitcoin");
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCOUNT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <span className="flex items-center gap-2">
                            {type.value === "crypto" && <Bitcoin className="h-3 w-3" />}
                            {type.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select value={accountCurrency} onValueChange={setAccountCurrency}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {accountType === "crypto" ? (
                        CRYPTO_CURRENCIES.map((curr) => (
                          <SelectItem key={curr.code} value={curr.code}>
                            {curr.symbol} {curr.code}
                          </SelectItem>
                        ))
                      ) : (
                        ALL_CURRENCIES.map((curr) => (
                          <SelectItem key={curr.code} value={curr.code}>
                            {curr.symbol} {curr.code}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="initialBalance">Balance</Label>
                  <Input
                    id="initialBalance"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    className="font-mono"
                    value={initialBalance}
                    onChange={(e) => setInitialBalance(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="balanceAsOfDate">As of Date</Label>
                  <Input
                    id="balanceAsOfDate"
                    type="date"
                    value={balanceAsOfDate}
                    onChange={(e) => setBalanceAsOfDate(e.target.value)}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Transactions before this date won't affect the balance calculation
              </p>

              {accountCurrency !== baseCurrency && (
                <div className="space-y-2 p-3 rounded-lg bg-muted/50 border border-border/50">
                  <Label htmlFor="manualRate">
                    Manual Exchange Rate <span className="text-xs text-muted-foreground">(optional override)</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">1 {accountCurrency} =</span>
                    <Input
                      id="manualRate"
                      type="number"
                      step="0.000001"
                      placeholder="Auto-fetch"
                      className="font-mono flex-1"
                      value={manualRate}
                      onChange={(e) => setManualRate(e.target.value)}
                    />
                    <span className="text-sm text-muted-foreground">{baseCurrency}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Leave empty to auto-fetch rates from ExchangeRate-API & CoinGecko
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Icon</Label>
                <div className="grid grid-cols-8 gap-2">
                  {ACCOUNT_ICONS.map((item) => {
                    const IconComponent = item.icon;
                    return (
                      <button
                        key={item.name}
                        type="button"
                        onClick={() => setSelectedIcon(item.name)}
                        className={`p-2 rounded-lg border-2 transition-all hover:scale-105 ${
                          selectedIcon === item.name
                            ? "border-primary bg-primary/10"
                            : "border-border hover:border-muted-foreground"
                        }`}
                        title={item.label}
                      >
                        <IconComponent className="h-5 w-5" />
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Parent Account (Optional)</Label>
                <Select 
                  value={parentAccountId || "none"} 
                  onValueChange={(value) => setParentAccountId(value === "none" ? null : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="None (Root Account)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Root Account)</SelectItem>
                    {accounts
                      .filter(a => a.id !== editingAccount?.id)
                      .map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          {account.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <Button type="submit" disabled={loading} className="w-full">
                {editingAccount ? "Update Account" : "Add Account"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {lastUpdated && accounts.some(a => a.currency !== baseCurrency) && (
        <div className="text-xs text-muted-foreground text-right">
          Rates last updated: {lastUpdated.toLocaleTimeString()}
        </div>
      )}

      {/* Summary Banner */}
      {accounts.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="rounded-xl bg-gradient-to-r from-primary to-primary/80 p-6 text-primary-foreground"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div>
              <p className="text-sm opacity-90 mb-1">Total Balance</p>
              <p className="text-2xl sm:text-3xl font-bold font-mono">{ratesReady ? formatCurrency(summaryTotals.totalBalance) : "..."}</p>
              {ratesReady && summaryTotals.totalExcluded.length > 0 && (
                <p className="text-xs opacity-90 mt-1" role="status">Excludes {describeExcluded(summaryTotals.totalExcluded)} (exchange rate unavailable)</p>
              )}
            </div>
            <div>
              <p className="text-sm opacity-90 mb-1">Crypto Holdings</p>
              <p className="text-2xl sm:text-3xl font-bold font-mono">{ratesReady ? formatCurrency(summaryTotals.cryptoHoldings) : "..."}</p>
              {ratesReady && summaryTotals.cryptoExcluded.length > 0 && (
                <p className="text-xs opacity-90 mt-1" role="status">Excludes {describeExcluded(summaryTotals.cryptoExcluded)} (price unavailable)</p>
              )}
            </div>
            <div>
              <p className="text-sm opacity-90 mb-1">Total Accounts</p>
              <p className="text-2xl sm:text-3xl font-bold">{summaryTotals.totalAccounts}</p>
            </div>
          </div>
        </motion.div>
      )}

      {accounts.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card className="border-dashed border-2 border-border/50">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/10 to-cyan/10 flex items-center justify-center mb-6 animate-pulse">
                <Wallet className="h-12 w-12 text-primary-text/60" />
              </div>
              <h3 className="text-xl font-bold mb-2">No accounts yet</h3>
              <p className="text-muted-foreground text-center mb-6 max-w-md">
                Add your first account to start tracking your finances across different sources.
              </p>
              <Button 
                onClick={() => setDialogOpen(true)}
                className="gap-2 bg-gradient-to-r from-primary to-secondary hover:opacity-90 transition-opacity shadow-lg"
              >
                <Plus className="h-4 w-4" />
                Add Your First Account
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <div className="grid gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {rootAccounts.map((account, index) => {
            const IconComponent = getIconComponent(account.icon);
            const childAccounts = getChildAccounts(account.id);

            return (
              <motion.div
                key={account.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300 border-border/40 flex flex-col min-h-[320px]">
                  {/* Card Header - Account Info */}
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-xl transition-transform duration-300 group-hover:scale-105 ${getTypeColor(account.account_type)}`}>
                          <IconComponent className="h-6 w-6" />
                        </div>
                        <div>
                          <CardTitle className="text-lg font-semibold">{account.name}</CardTitle>
                          <p className="text-sm text-muted-foreground">
                            {ACCOUNT_TYPES.find(t => t.value === account.account_type)?.label || account.account_type}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEdit(account)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 hover:text-destructive"
                          onClick={() => handleDelete(account.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0 flex-1 flex flex-col">
                    {/* Balance Section */}
                    {(() => {
                      const calculatedBalance = getAccountBalance({ id: account.id, name: account.name, initial_balance: account.initial_balance });
                      return (
                        <div className="mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm text-muted-foreground">Balance</span>
                            {account.account_type === "investment" && (
                              <BalanceHistory
                                accountId={account.id}
                                accountName={account.name}
                                currentBalance={calculatedBalance}
                                currencySymbol={getCurrencyInfo(account.currency)?.symbol || "$"}
                              />
                            )}
                          </div>
                          <div className="text-2xl font-bold font-mono">
                            {getCurrencyInfo(account.currency)?.symbol}
                            {formatNumber ? formatNumber(calculatedBalance) : calculatedBalance.toLocaleString(preferences?.locale || undefined, { 
                              minimumFractionDigits: isCrypto(account.currency) ? 4 : 2,
                              maximumFractionDigits: isCrypto(account.currency) ? 8 : 2 
                            })}
                          </div>
                          {account.currency !== baseCurrency && (
                            <div className="text-sm text-muted-foreground font-mono mt-1">
                              ≈ {formatConverted(convertToBase(calculatedBalance, account.currency, account.manual_exchange_rate))}
                              {account.manual_exchange_rate && (
                                <span className="ml-1 text-amber-500">(manual)</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Wallets Section - takes flex space */}
                    {(() => {
                      const accountWallets = getAccountWallets(account.id);
                      return (
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                              <Coins className="h-4 w-4" />
                              Wallets
                            </p>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => handleAddWallet(account.id)}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add
                            </Button>
                          </div>
                          {accountWallets.length > 0 ? (
                            <div className="space-y-2">
                              {accountWallets.map((wallet) => {
                                const walletCurrencyInfo = getCurrencyInfo(wallet.currency);
                                const calculatedWalletBalance = getWalletBalance({ id: wallet.id, account_id: wallet.account_id, name: wallet.name, balance: wallet.balance }, account.name);
                                return (
                                  <div
                                    key={wallet.id}
                                    className="flex items-center justify-between p-3 rounded-lg bg-muted/40 group/wallet hover:bg-muted/60 transition-colors"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded-md bg-background flex items-center justify-center border border-border/50">
                                        <span className="text-xs font-mono font-semibold">{wallet.currency.slice(0, 2)}</span>
                                      </div>
                                      <div>
                                        <span className="text-sm font-medium">{wallet.name}</span>
                                        {account.account_type === "investment" && (
                                          <div className="inline-block ml-2">
                                            <BalanceHistory
                                              walletId={wallet.id}
                                              accountName={`${account.name} - ${wallet.name}`}
                                              currentBalance={calculatedWalletBalance}
                                              currencySymbol={walletCurrencyInfo?.symbol || "$"}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className="text-right">
                                        <div className="text-sm font-mono font-medium">
                                          {walletCurrencyInfo?.symbol}{formatNumber ? formatNumber(calculatedWalletBalance) : calculatedWalletBalance.toLocaleString(preferences?.locale || undefined, {
                                            minimumFractionDigits: isCrypto(wallet.currency) ? 4 : 2,
                                            maximumFractionDigits: isCrypto(wallet.currency) ? 8 : 2
                                          })}
                                        </div>
                                        {wallet.currency !== baseCurrency && (
                                          <div className="text-xs text-muted-foreground font-mono">
                                            {formatConverted(convertToBase(calculatedWalletBalance, wallet.currency, wallet.manual_exchange_rate))}
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex gap-1 opacity-0 group-hover/wallet:opacity-100 transition-opacity">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6"
                                          onClick={() => handleEditWallet(wallet)}
                                        >
                                          <Pencil className="h-3 w-3" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6 hover:text-destructive"
                                          onClick={() => handleDeleteWallet(wallet.id)}
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-muted-foreground italic text-center py-4 bg-muted/20 rounded-lg">
                              No wallets yet
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Funding Sources */}
                    {(() => {
                      const accountFS = allFundingSources
                        .filter(fs => fs.account_id === account.id)
                        .sort((a, b) => a.priority - b.priority);
                      const linkedIds = new Set(accountFS.map(fs => fs.funding_account_id));
                      const availableAccounts = accounts.filter(a => a.id !== account.id && !linkedIds.has(a.id));
                      const filteredAvailable = addFSForAccountId === account.id && addFSSearch
                        ? availableAccounts.filter(a => a.name.toLowerCase().includes(addFSSearch.toLowerCase()))
                        : availableAccounts;

                      return (
                        <div className="pt-3 mt-3 border-t border-border/30">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm text-muted-foreground font-medium flex items-center gap-1.5">
                              <Link2 className="h-4 w-4" />
                              Funding Sources
                            </p>
                          </div>

                          {accountFS.length > 0 && (
                            <div className="space-y-1 mb-2">
                              {accountFS.map((fs, idx) => {
                                const fundingAcc = accounts.find(a => a.id === fs.funding_account_id);
                                if (!fundingAcc) return null;
                                return (
                                  <div
                                    key={fs.id}
                                    className="flex items-center justify-between p-2 rounded-lg bg-muted/40 group/fs hover:bg-muted/60 transition-colors"
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-mono text-muted-foreground w-4 shrink-0">
                                        {idx + 1}.
                                      </span>
                                      <span className="text-sm">{fundingAcc.name}</span>
                                      {idx === 0 && (
                                        <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary-text font-medium">Primary</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-0.5 opacity-0 group-hover/fs:opacity-100 transition-opacity">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6"
                                        disabled={idx === 0}
                                        onClick={() => reorderFundingSourceMutation.mutate(
                                          { id: fs.id, accountId: account.id, direction: 'up' },
                                          { onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }) }
                                        )}
                                      >
                                        <ChevronUp className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6"
                                        disabled={idx === accountFS.length - 1}
                                        onClick={() => reorderFundingSourceMutation.mutate(
                                          { id: fs.id, accountId: account.id, direction: 'down' },
                                          { onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }) }
                                        )}
                                      >
                                        <ChevronDown className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 hover:text-destructive"
                                        onClick={() => removeFundingSourceMutation.mutate(
                                          { id: fs.id, accountId: account.id },
                                          { onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }) }
                                        )}
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {addFSForAccountId === account.id ? (
                            <div className="mt-1">
                              <div className="relative">
                                <Input
                                  placeholder="Search accounts..."
                                  value={addFSSearch}
                                  onChange={(e) => setAddFSSearch(e.target.value)}
                                  className="h-8 text-xs pr-7"
                                  autoFocus
                                  onBlur={() => setTimeout(() => setAddFSForAccountId(null), 150)}
                                />
                                <button
                                  type="button"
                                  onClick={() => setAddFSForAccountId(null)}
                                  className="absolute right-2 top-1/2 -translate-y-1/2"
                                >
                                  <X className="h-3 w-3 text-muted-foreground" />
                                </button>
                              </div>
                              {filteredAvailable.length > 0 && (
                                <div className="mt-1 rounded-md border bg-popover p-1 shadow-md max-h-[150px] overflow-y-auto">
                                  {filteredAvailable.slice(0, 8).map(a => (
                                    <button
                                      key={a.id}
                                      type="button"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => {
                                        setAddFSForAccountId(null);
                                        setAddFSSearch("");
                                        addFundingSourceMutation.mutate(
                                          { accountId: account.id, fundingAccountId: a.id },
                                          {
                                            onError: (e: any) => toast({ title: "Error adding funding source", description: e.message, variant: "destructive" }),
                                          }
                                        );
                                      }}
                                      className="flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded-sm hover:bg-accent text-left"
                                    >
                                      {a.name}
                                    </button>
                                  ))}
                                </div>
                              )}
                              {filteredAvailable.length === 0 && (
                                <p className="text-xs text-muted-foreground text-center py-2">No accounts available</p>
                              )}
                            </div>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs w-full justify-start gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => {
                                setAddFSForAccountId(account.id);
                                setAddFSSearch("");
                              }}
                            >
                              <Plus className="h-3 w-3" />
                              Add funding source
                            </Button>
                          )}
                        </div>
                      );
                    })()}

                    {/* Sub-accounts */}
                    {childAccounts.length > 0 && (
                      <div className="pt-3 mt-3 border-t border-border/30 space-y-2">
                        <p className="text-sm text-muted-foreground font-medium">Sub-accounts</p>
                        {childAccounts.map((child) => {
                          const ChildIcon = getIconComponent(child.icon);
                          const childCurrencyInfo = getCurrencyInfo(child.currency);
                          return (
                            <div
                              key={child.id}
                              className="flex items-center justify-between p-3 rounded-lg bg-muted/30 group/child hover:bg-muted/50 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <ChildIcon className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">{child.name}</span>
                                {child.currency !== baseCurrency && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-muted/50 font-mono">
                                    {child.currency}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-mono">
                                  {childCurrencyInfo?.symbol}{child.initial_balance.toLocaleString()}
                                </span>
                                <div className="flex gap-1 opacity-0 group-hover/child:opacity-100 transition-opacity">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => handleEdit(child)}
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 hover:text-destructive"
                                    onClick={() => handleDelete(child.id)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Card Footer - Dashboard Toggle */}
                    <div className="mt-auto pt-4 border-t border-border/30 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Show in Dashboard</span>
                      </div>
                      <Switch
                        checked={!account.exclude_from_dashboard}
                        onCheckedChange={() => handleToggleDashboardVisibility(account)}
                      />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Wallet Dialog */}
      <Dialog open={walletDialogOpen} onOpenChange={(open) => {
        setWalletDialogOpen(open);
        if (!open) resetWalletForm();
      }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <Coins className="h-5 w-5" />
              {editingWallet ? "Edit Wallet" : "Add Wallet"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleWalletSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="walletName">Wallet Name</Label>
              <Input
                id="walletName"
                placeholder="e.g., USD Balance, BTC Wallet"
                value={walletName}
                onChange={(e) => setWalletName(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={walletCurrency} onValueChange={setWalletCurrency}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ALL_CURRENCIES.map((curr) => (
                      <SelectItem key={curr.code} value={curr.code}>
                        {curr.symbol} {curr.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="walletBalance">Balance</Label>
                <Input
                  id="walletBalance"
                  type="number"
                  step="0.00000001"
                  placeholder="0.00"
                  className="font-mono"
                  value={walletBalance}
                  onChange={(e) => setWalletBalance(e.target.value)}
                />
              </div>
            </div>

            {walletCurrency !== baseCurrency && (
              <div className="space-y-2 p-3 rounded-lg bg-muted/50 border border-border/50">
                <Label htmlFor="walletManualRate">
                  Manual Rate <span className="text-xs text-muted-foreground">(optional)</span>
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">1 {walletCurrency} =</span>
                  <Input
                    id="walletManualRate"
                    type="number"
                    step="0.000001"
                    placeholder="Auto"
                    className="font-mono flex-1"
                    value={walletManualRate}
                    onChange={(e) => setWalletManualRate(e.target.value)}
                  />
                  <span className="text-sm text-muted-foreground">{baseCurrency}</span>
                </div>
              </div>
            )}

            <Button type="submit" disabled={loading} className="w-full">
              {editingWallet ? "Update Wallet" : "Add Wallet"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
